import React, { useCallback, useEffect, useRef } from 'react';
import Modal, { ModalBody, ModalTransition } from '@atlaskit/modal-dialog';
import Spinner from '@atlaskit/spinner';
import { useFeatureGate } from '@atlassian/feature-gates-react';

import { AidFrontendFeatureGate } from '../providers/FeatureGates';
import { useAppDispatch, useAppSelector } from '../stores/redux.store';
import { awsCaptchaPuzzleCompleted, closeAwsCaptchaModal } from './aws-captcha-actions';
import { useAwsWafCaptchaSdkScript } from './use-aws-waf-captcha-sdk-script';
import { useAwsWafConfig } from './use-aws-waf-config';

import { AWS_CAPTCHA_CONTAINER_ID } from './aws-captcha.constants';

type AWSWafCaptchaComponentProps = {
  /** Callback that is called upon any action that indicates completion of the puzzle (success, error) */
  onDone?: () => void;
  /** Callback that is called upon success callback being triggered of the AWS Captcha puzzle */
  onSuccess?: () => void;
  /** Callback that is called upon error callback being triggered of the AWS Captcha puzzle */
  onError?: () => void;
};

const AWSWafCaptchaComponent = ({ onDone, onSuccess, onError }: AWSWafCaptchaComponentProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useAppDispatch();

  const isFeatureEnabled = useFeatureGate(AidFrontendFeatureGate.ENABLE_AWS_WAF_CAPTCHA);
  const awsWafConfig = useAwsWafConfig();
  const activeConfig = isFeatureEnabled ? awsWafConfig : null;

  const { hasLoaded: scriptHasLoaded } = useAwsWafCaptchaSdkScript(activeConfig);

  useEffect(() => {
    if (!activeConfig || !scriptHasLoaded) {
      return;
    }
    const container = containerRef.current;
    if (!container) return;

    if (!window.AwsWafCaptcha) {
      return;
    }

    window.AwsWafCaptcha?.renderCaptcha?.(container, {
      apiKey: activeConfig.apiKey,
      onSuccess: () => {
        dispatch(awsCaptchaPuzzleCompleted());
        onDone?.();
        onSuccess?.();
      },
      onError: (error: unknown) => {
        console.log('AWS Captcha Error: ', error);
        dispatch(closeAwsCaptchaModal());
        onDone?.();
        onError?.();
      },
    });
  }, [dispatch, activeConfig, onDone, onError, onSuccess, scriptHasLoaded]);

  if (!activeConfig) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      data-testid="aws-captcha-modal-content-idf-testid"
      id={AWS_CAPTCHA_CONTAINER_ID}>
      {/* The Captcha SDK JS will replace this once loaded */}
      <Spinner size="large" />
    </div>
  );
};

/**
 * Conditionally render the AWS Captcha modal depending on:
 * 1. Whether the AWS Captcha feature is enabled
 * 2. Whether the puzzle is currently opened (i.e. the user has been challenged and needs to complete a puzzle to continue)
 */
export const MaybeAWSWafCaptchaModal = () => {
  const isFeatureEnabled = useFeatureGate(AidFrontendFeatureGate.ENABLE_AWS_WAF_CAPTCHA);
  const awsWafConfig = useAwsWafConfig();

  const { isCaptchaPuzzleModalOpen, isPuzzleCompleted } = useAppSelector(state => state.awsCaptcha);

  const dispatch = useAppDispatch();

  const onClose = useCallback(() => {
    if (isPuzzleCompleted) {
      // User completed puzzle, close modal and show completion message
      dispatch(closeAwsCaptchaModal());
    }
  }, [dispatch, isPuzzleCompleted]);

  if (!isFeatureEnabled || !awsWafConfig) {
    return null;
  }

  if (!isCaptchaPuzzleModalOpen) {
    return null;
  }

  return (
    <ModalTransition>
      <Modal
        width="medium"
        testId="aws-captcha-modal-idf-testid"
        onClose={onClose}
        // Prevent user from accidentally closing the modal (with the captcha puzzle in it)
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEscapePress={false}>
        <ModalBody testId="aws-captcha-modal-body-idf-testid">
          <div data-testid="aws-captcha-modal-container-idf-testid">
            <AWSWafCaptchaComponent />
          </div>
        </ModalBody>
      </Modal>
    </ModalTransition>
  );
};
